import React, { useEffect, useRef, useState } from 'react';

import '@toast-ui/editor/dist/toastui-editor.css';
import '@toast-ui/editor/dist/i18n/ko-kr';
import { Editor } from '@toast-ui/react-editor';
import colorSyntax from '@toast-ui/editor-plugin-color-syntax';
import 'tui-color-picker/dist/tui-color-picker.css';
import '@toast-ui/editor-plugin-color-syntax/dist/toastui-editor-plugin-color-syntax.css';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import 'moment/locale/ko';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { useRecoilValue } from 'recoil';
import { authAtom } from '_state';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { authUtils } from '../../utils/authUtils';
import Dropzone from '../../components/Inputs/Dropzone';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import DateAdapter from '@mui/lab/AdapterMoment';
import DatePicker from '@mui/lab/DatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import Stack from '@mui/material/Stack';

const columns = [{ id: 'fileName', label: '첨부파일', minWidth: 40, width: 400, align: 'left' }];

export default function Register(props) {
  const { state, modalState, onBack: onModalBack, onSuccess, hideTitle } = props;
  const [datas, setDatas] = useState({});
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    setError,
    formState: { errors, isDirty }
  } = useForm({});

  const auth = useRecoilValue(authAtom);

  const navigate = useNavigate();
  const location = useLocation();
  const { name, kind, id, file } = modalState || location.state;
  const editorRef = useRef();
  let formData = new FormData();

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  const handleBack = () => {
    if (onModalBack) {
      onModalBack();
    } else {
      navigate(-1);
    }
  };

  const handleChange = (e) => {
    setDatas({ ...datas, [e.target.id]: e.target.value });
  };

  const registerOrModify = async (formData, config) => {
    if (state === 'register' || state === 'reply') return await registerBoard(formData, config);
    return await modifyBoard(formData, config);
  };

  const registerBoard = async (formData, config) => {
    return await axios.post(`/api/board`, formData, config);
  };

  const modifyBoard = async (formData, config) => {
    return await axios.patch(`/api/board/${kind}/${datas.id}`, formData, config);
  };

  const onSubmit = (data) => {
    const config = {
      headers: { 'content-type': 'multipart/form-data' }
    };
    formData.append('state', state);
    formData.append('kind', kind);
    if (auth?.userid) {
      formData.append('createdId', auth.userid);
    } else {
      // 비회원: guest 필드 전송 (수정 시 비밀번호는 sessionStorage에서 가져옴)
      formData.append('guestName',    datas.guestName    || '');
      formData.append('guestCompany', datas.guestCompany || '');
      formData.append('guestPhone',   datas.guestPhone   || '');
      formData.append('guestEmail',   datas.guestEmail   || '');
      const pw = state === 'modify'
        ? sessionStorage.getItem('guestPw') || ''
        : datas.guestPassword || '';
      formData.append('guestPassword', pw);
      if (state === 'modify') sessionStorage.removeItem('guestPw');
    }
    formData.append('subject', datas.subject);
    formData.append('content', editorRef.current?.getInstance().getHTML());
    formData.append('isSecret', datas.isSecret);
    formData.append('isNotice', datas.isNotice);
    formData.append('startDate', startDate);
    formData.append('endDate', endDate);
    if (state === 'reply') {
      formData.append('parentId', id);
    }

    config.headers['Content-Length'] = editorRef.current?.getInstance().getHTML().length;

    registerOrModify(formData, config)
      .then((response) => {
        alert(response.data.message);
        if (onSuccess) {
          onSuccess({ kind: response.data.kind });
        } else {
          navigate(`/${response.data.kind}`);
        }
      })
      .catch((error) => {
        alert(error.response.data.message);
      });
  };

  const handleFileDelete = (fileId) => {
    axios
      .delete(`/api/board/file/${fileId}`)
      .then((response) => {
        alert(response.data.message);
        axios.get(`/api/board/${kind}/${id}`).then((response) => {
          setDatas({ ...datas, files: response.data.files });
        });
      })
      .catch((error) => {
        alert(error.response.data.message);
      });
  };

  const getBoardDetail = async () => {
    const response = await axios.get(`/api/board/${kind}/${id}`);
    await setDatas(response.data);
    await setStartDate(response.data.startDate);
    await setEndDate(response.data.endDate);
    await setValue('datas', response.data);

    // 비회원 수정 시 guest 필드를 react-hook-form에도 등록해 validation이 정상 동작하게 함
    if (response.data.guestName)    setValue('guestName',    response.data.guestName);
    if (response.data.guestEmail)   setValue('guestEmail',   response.data.guestEmail);
    if (response.data.guestCompany) setValue('guestCompany', response.data.guestCompany);
    if (response.data.guestPhone)   setValue('guestPhone',   response.data.guestPhone);

    const htmlToEditor = response.data?.content;
    editorRef.current?.getInstance().setHTML(htmlToEditor);
  };

  const handleChangeCheckbox = (e) => {
    setDatas({ ...datas, [e.target.name]: e.target.checked });
  };

  useEffect(async () => {
    if (state === 'modify') {
      await getBoardDetail();
    }
  }, []);

  return (
    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
      {!hideTitle && (
        <Typography component="h2" variant="h6" color="primary" gutterBottom>
          {name} {state === 'register' ? '등록' : state === 'modify' ? '수정' : '답글'}
        </Typography>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ mt: 1 }}>
            <TextField
              id="subject"
              variant="outlined"
              label="제목"
              sx={{ width: 3.5 / 5, ml: 1 }}
              value={datas?.subject || ''}
              error={!!errors.datas?.subject}
              helperText={errors.datas?.subject && errors.datas?.subject.message}
              {...register('datas.subject', {
                required: '제목을 입력해주세요.',
                maxLength: { value: 200, message: '제목은 200자 이하입니다.' }
              })}
              onChange={handleChange}
            />
            {/* 비로그인 사용자: 비회원 정보 입력 섹션 */}
            {!auth && (
              <Box sx={{ mt: 2, ml: 1, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, width: 3.5 / 5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  비회원 정보 입력 (이름, 이메일, 비밀번호 필수)
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                  <TextField
                    id="guestName"
                    label="이름 *"
                    size="small"
                    error={!!errors.guestName}
                    helperText={errors.guestName?.message}
                    value={datas?.guestName || ''}
                    {...register('guestName', {
                      required: '이름을 입력해주세요.',
                      maxLength: { value: 100, message: '이름은 100자 이하입니다.' }
                    })}
                    onChange={handleChange}
                  />
                  <TextField
                    id="guestEmail"
                    label="이메일 *"
                    size="small"
                    error={!!errors.guestEmail}
                    helperText={errors.guestEmail?.message}
                    value={datas?.guestEmail || ''}
                    {...register('guestEmail', {
                      required: '이메일을 입력해주세요.',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: '이메일 형식이 올바르지 않습니다.' }
                    })}
                    onChange={handleChange}
                  />
                  {state !== 'modify' && (
                    <TextField
                      id="guestPassword"
                      label="비밀번호 *"
                      type="text"
                      size="small"
                      error={!!errors.guestPassword}
                      helperText={errors.guestPassword?.message}
                      value={datas?.guestPassword || ''}
                      {...register('guestPassword', {
                        required: '비밀번호를 입력해주세요.',
                        minLength: { value: 4, message: '비밀번호는 4자 이상입니다.' }
                      })}
                      onChange={handleChange}
                    />
                  )}
                </Stack>
                <Stack direction="row" spacing={2}>
                  <TextField
                    id="guestCompany"
                    label="소속"
                    size="small"
                    value={datas?.guestCompany || ''}
                    {...register('guestCompany', { maxLength: { value: 100, message: '소속은 100자 이하입니다.' } })}
                    onChange={handleChange}
                  />
                  <TextField
                    id="guestPhone"
                    label="전화번호"
                    size="small"
                    value={datas?.guestPhone || ''}
                    {...register('guestPhone', { maxLength: { value: 20, message: '전화번호는 20자 이하입니다.' } })}
                    onChange={handleChange}
                  />
                </Stack>
              </Box>
            )}
            {kind.toUpperCase() !== 'SECRET' && kind.toUpperCase() !== 'NOTICE' && (
              <FormControlLabel control={<Checkbox />} id="isSecret" name="isSecret" label="비밀글" onChange={handleChangeCheckbox} checked={datas.isSecret} sx={{ ml: 1 }} />
            )}
            {authUtils.useIsAdmin() && kind.toUpperCase() !== 'NOTICE' && <FormControlLabel control={<Checkbox />} id="isSecret" name="isNotice" label="공지사항" onChange={handleChangeCheckbox} checked={datas.isNotice} />}
            {kind.toUpperCase() === 'NOTICE' && (
              <Stack direction="row" spacing={1} sx={{ ml: 1, mt: 1, width: 3.5 / 5 }}>
                <LocalizationProvider dateAdapter={DateAdapter}>
                  <DatePicker
                    inputFormat={'yyyy-MM-DD'}
                    value={startDate}
                    onChange={(newValue) => {
                      setStartDate(newValue);
                    }}
                    label="게시 시작일"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        sx={{ maxWidth: 200 }}
                        error={!!errors.startDate}
                        helperText={errors.startDate && errors.startDate.message}
                        {...register('startDate', {
                          required: '시작일 입력해주세요.'
                        })}
                      />
                    )}
                    disableMaskedInput={true}
                    disablePast={true}
                  />
                </LocalizationProvider>
                <Typography component="h1" variant="body1">
                  ~
                </Typography>
                <LocalizationProvider dateAdapter={DateAdapter}>
                  <DatePicker
                    inputFormat={'yyyy-MM-DD'}
                    value={endDate}
                    onChange={(newValue) => {
                      if (newValue < startDate) {
                        setError('endDate', { type: 'nonPast', message: '종료일이 시작일보다 빠를수 없습니다.' });
                        return;
                      }
                      setEndDate(newValue);
                    }}
                    label="게시 종료일"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        sx={{ maxWidth: 200 }}
                        error={!!errors.endDate}
                        helperText={errors.endDate && errors.endDate.message}
                        {...register('endDate', {
                          required: '종료일을 입력해주세요.'
                        })}
                      />
                    )}
                    disableMaskedInput={true}
                    disablePast={true}
                  />
                </LocalizationProvider>
              </Stack>
            )}
            <br />
            <Box component="div" sx={{ maxWidth: 1200, minHeight: 500, ml: 1, overflowY: 'auto' }}>
              <Editor previewStyle="vertical" height="800px" ref={editorRef} initialEditType={'wysiwyg'} plugins={[colorSyntax]} language={'ko-KR'} />
            </Box>
            <br />
            {!!file && (
              <Box component="div" sx={{ maxWidth: 1200, ml: 1, overflowY: 'auto' }}>
                <Dropzone id="file" maxFilesize={5242880} formData={formData} />
              </Box>
            )}
            {!!datas.files && (
              <Box>
                <TableContainer component={'div'}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {columns.map((column) => (
                          <>
                            <TableCell key={column.id} align={column.align} sx={{ minWidth: column.minWidth, width: column.width }}>
                              {column.label}
                            </TableCell>
                            <TableCell></TableCell>
                          </>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {datas.files.map((row, rowIdx) => (
                        <TableRow hover key={row.id}>
                          {columns.map((col, colIdx) => (
                            <>
                              <TableCell key={`row${rowIdx}-col${colIdx}`} align={col.align}>
                                {row[col.id]}
                              </TableCell>
                              <TableCell key={`row${rowIdx}-down`} align="left">
                                <Tooltip title={`파일 삭제`}>
                                  <IconButton
                                    aria-label="down"
                                    onClick={(e) => {
                                      handleFileDelete(row.id);
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
            <Button type="submit" sx={{ width: 200, ml: 1 }} variant="contained">
              {state === 'register' ? '등록' : state === 'modify' ? '수정' : '답글 등록'}
            </Button>
            <Button sx={{ width: 200, ml: 1 }} variant="contained" color={'warning'} onClick={handleBack}>
              취소
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}
